import React from "react";
import { Box, Typography } from "@mui/material";

const PrivacyPolicy = () => {
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Privacy Policy and Site Use Rules
      </Typography>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Disclaimer and Privacy Policy
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
        All general information is provided for informational purposes only and
        is not a guide to action. To receive individualized treatment protocols
        and instructions for use, you should consult a medical specialist. In
        addition, the terms of use of your data are set out below.
      </Typography>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Key Provisions of Our Privacy Policy
      </Typography>
      <Box component="ol" sx={{ pl: 3, mb: 3 }}>
        <li>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            We are committed to never misusing the personal data we collect from
            you.
          </Typography>
        </li>
        <li>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            We undertake never to share your data or contact information with
            third parties except as required by law.
          </Typography>
        </li>
      </Box>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Privacy Policy Rationale
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
        One of the primary principles of our company is the utmost protection
        of any personal information of our partners and clients. Thus, this
        privacy policy is the basis on which we undertake not to distribute your
        personal information transmitted to us during your visit to the portal.
        The condition of strict confidentiality applies to any data that we
        receive about visitors to the portal and which, even theoretically,
        correlates with a specific person. A similar condition also applies to
        our partners who have entered into obligatory relationships with us.
      </Typography>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Receipt and Processing of Personal Data
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
        Personal data is information that, in one way or another, indicates
        your identity. This can include your first and last name, postal or
        email addresses, and similar details. Our company never requires your
        personal data except in situations where it is necessary for our
        cooperation, for example when registering on the site, activating
        certain services and software products, or placing orders.
      </Typography>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Use and Publication of Personal Data
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
        We use the information we collect about you solely to solve technical
        problems, for example to provide the ability to use special sections or
        to communicate with you. Our company undertakes never to disclose your
        personal information to third parties. This agreement is binding on all
        of our employees. All personal data collected by our site belongs solely
        to you.
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
        However, by providing us with information about yourself, you agree to
        allow us to use it for a lawful purpose, including but not limited to
        the following:
      </Typography>
      <Box component="ul" sx={{ pl: 3, mb: 3 }}>
        <li>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            When placing orders on the website.
          </Typography>
        </li>
        <li>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            When disclosing your personal information to third parties while
            placing orders for goods or services provided by them using our
            online resource.
          </Typography>
        </li>
        <li>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            To carry out marketing activities using email, contextual banners,
            pop-ups, and similar methods.
          </Typography>
        </li>
        <li>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            When monitoring the implementation of the User Agreement.
          </Typography>
        </li>
        <li>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            To optimize the functionality and content of the portal.
          </Typography>
        </li>
        <li>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            During feedback.
          </Typography>
        </li>
      </Box>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Data Collected Automatically
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
        When visiting the website, some non-personal information is
        automatically recorded. These may include:
      </Typography>
      <Box component="ul" sx={{ pl: 3, mb: 2 }}>
        <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Browser program identifier.</Typography></li>
        <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>IP address.</Typography></li>
        <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Operating system type.</Typography></li>
        <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Internet provider domain information.</Typography></li>
        <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>List of visited pages.</Typography></li>
        <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Products you reviewed in the catalog.</Typography></li>
        <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Frequency of site visits.</Typography></li>
        <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Average time spent on the site.</Typography></li>
      </Box>
      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
        The purpose of collecting such data is to obtain statistics that allow
        us to optimize the functionality of the site, as well as improve and
        expand its content. This also allows us to personalize your experience
        on the site, for example through the use of cookies.
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
        Cookies are small files that are written to your computer's hard drive
        and stored there for a period of time. With their help, our website can
        recognize you the next time you visit, immediately set the settings you
        need, and take your preferences into account. All this information, like
        confidential data, is not transferred to anyone else.
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
        The company also informs its users that it maintains statistics using
        aggregated information about users and, in addition, may use it for any
        purpose not prohibited by law. Aggregated information represents
        generalized information about users, devoid of individual features, and
        combined into groups based on certain characteristics.
      </Typography>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Safety
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
        We do everything in our power to ensure the most effective protection
        of your personal information. Information about you is carefully
        protected from the possibility of loss or erasure, from unauthorized
        changes, and from the risks of unauthorized access, use, or
        distribution. However, you must understand that absolute security of
        information cannot be guaranteed by any method of data exchange. In
        addition, you are responsible for maintaining the privacy of your
        identity and any other confidential information. You control the amount
        of information that you transmit to us when using the site.
      </Typography>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Feedback
      </Typography>
      <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
        If you have any questions about the information policy of the site, the
        protection of information posted here, or the use of your personal data,
        we suggest that you contact us.
      </Typography>

      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Agreement
      </Typography>
      <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
        The fact of using our portal, as well as receiving information from us
        by email, constitutes your agreement with the privacy policy set out on
        this page. The company has the right at any time to make modifications,
        additions, or reductions in the privacy policy at its own discretion.
        Such changes are considered effective immediately from the moment they
        are posted on the portal. We recommend that you visit this page
        periodically to stay informed about possible updates. If you continue to
        use our portal after changes to the privacy policy are published here,
        this means automatic consent to all modifications made.
      </Typography>
    </Box>
  );
};

export default PrivacyPolicy;
